// src/components/chat/WebFetchItem.tsx

import * as React from "react";
import { ExternalLink } from "lucide-react";
import { WebFetchToolEvent } from "../../interfaces/webToolTypes";
import { createLogger } from "../../utils/logger";

const log = createLogger("WebFetchItem");

interface WebFetchItemProps {
  event: WebFetchToolEvent;
  isLast: boolean;
  showConnector: boolean;
}

/**
 * Component to display a single web fetch event.
 * Renders both success and error states with the same layout:
 * favicon icon + label + content text + domain.
 */
const WebFetchItem: React.FC<WebFetchItemProps> = ({ event, isLast, showConnector }) => {
  const [faviconError, setFaviconError] = React.useState(false);
  const isError = event.status === "error";

  // Extract domain from URL
  const domain = React.useMemo(() => {
    try {
      return new URL(event.url).hostname;
    } catch {
      log.warn("Failed to parse URL", { url: event.url });
      return "unknown";
    }
  }, [event.url]);

  // Generate favicon URL using Google's S2 service (16px to match globe icon)
  const faviconUrl = React.useMemo(() => {
    return `https://www.google.com/s2/favicons?domain=${domain}&sz=16`;
  }, [domain]);

  // Main container using flexbox columns (matching WebSearchItem)
  const eventContainerStyle: React.CSSProperties = {
    display: "flex",
    gap: "12px",
    paddingBottom: isLast ? "0" : "16px",
  };

  // Icon column that contains the favicon and connector line
  const iconColumnStyle: React.CSSProperties = {
    width: "24px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    position: "relative",
  };

  // Icon container for favicon
  const iconStyle: React.CSSProperties = {
    width: "24px",
    height: "24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2,
    backgroundColor: "#FFFFFF",
    borderRadius: "4px",
  };

  // Connector line that spans the full height of the icon column
  const connectorLineStyle: React.CSSProperties = {
    position: "absolute",
    top: "24px",
    bottom: isLast ? "0" : "-16px",
    left: "50%",
    width: "2px",
    marginLeft: "-1px",
    borderLeft: "2px dashed #D1D5DB",
    display: showConnector ? "block" : "none",
  };

  // Content column
  const contentColumnStyle: React.CSSProperties = {
    flex: 1,
    minWidth: 0,
  };

  // Inline content row (no box, no border, no background)
  const contentRowStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    padding: "4px 0",
    cursor: "pointer",
  };

  // Label style shared by both states
  const labelStyle: React.CSSProperties = {
    fontSize: "11px",
    fontWeight: 600,
    color: "#9CA3AF",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    marginRight: "8px",
    flexShrink: 0,
  };

  const titleStyle: React.CSSProperties = {
    flex: 1,
    fontSize: "13px",
    color: isError ? "#6B7280" : "#374151",
    fontWeight: isError ? 400 : 500,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    marginRight: "8px",
  };

  const linkContainerStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    fontSize: "12px",
    color: "#6B7280",
    flexShrink: 0,
  };

  // Determine display text
  const label = isError ? "Couldn't fetch" : "Fetched";
  const displayText = isError
    ? event.error_message || "Unknown error"
    : event.title || "Untitled page";
  const titleAttr = isError ? event.error_message || "Unknown error" : event.title || event.url;

  return (
    <div style={eventContainerStyle}>
      {/* Icon column — favicon for both success and error */}
      <div style={iconColumnStyle}>
        <div style={iconStyle}>
          {!faviconError ? (
            <img
              src={faviconUrl}
              alt=""
              style={{ width: "16px", height: "16px", borderRadius: "2px" }}
              onError={() => setFaviconError(true)}
            />
          ) : (
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect x="3" y="3" width="18" height="18" rx="2" stroke="#9CA3AF" strokeWidth="2" />
              <path
                d="M8 8h8M8 12h6M8 16h4"
                stroke="#9CA3AF"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          )}
        </div>
        <div style={connectorLineStyle} />
      </div>

      {/* Content column — inline text, no nested box */}
      <div style={contentColumnStyle}>
        <div
          style={contentRowStyle}
          onClick={() => window.open(event.url, "_blank", "noopener,noreferrer")}
        >
          <span style={labelStyle}>{label}</span>
          <span style={titleStyle} title={titleAttr}>
            {displayText}
          </span>
          <div style={linkContainerStyle}>
            <span>{domain}</span>
            <ExternalLink size={14} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default WebFetchItem;
